// LineChart.js
import React, { useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { VerticalAxis } from "./VerticalAxis";
import { Divider, styled, useMediaQuery, useTheme } from "@mui/material";
import { dateToUsShortFormat, getWeekStartAndEndDates } from "utils/helpers";
import { getFridayDateByYearAndWeek } from "utils/getFridayDateByYearAndWeek";

export const CHART_HEIGHT = 200;
export const TABLE_HEIGHT = 260;
export const COLUMN_WIDTH = 100;
export const VERTICAL_AXIS_WIDTH = 100;

const ContainerWithTransparentScroll = styled("div")`
  overflow-x: scroll;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none;
    width: 0 !important;
  }
`;

export const normalizeDateByPeriod = (date, period) => {
  if (period === "Yearly") {
    return date.substr(0, 4);
  };
  if (period === "Monthly") {
    const currentDate = new Date(date);
    const month = currentDate.toLocaleString("default", { month: "short" });
    return `${month} ${currentDate.getFullYear()}`;
  }
  if (period === "Weekly") {
    const week = date.substr(4, 2);
    const year = date.substr(0, 4);
    const { start, end } = getWeekStartAndEndDates(week, year);
    return `${dateToUsShortFormat(start)} - ${dateToUsShortFormat(
      end
    )}`;
  }
  return dateToUsShortFormat(date);
};

const shortWeek = (date) => {
  const week = Number(date.substr(4, 2));
  const year = Number(date.substr(0, 4));
  return getFridayDateByYearAndWeek(year, week);
};

export const LineChart = ({ data, loading, period = "Daily" }) => {
  const scrollChartRef = React.useRef(null);
  const scrollTableRef = React.useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const dates = data.map((item) => normalizeDateByPeriod(item[0], period));
  const weeks = data.map((item) => shortWeek(item[0]));

  const totalCanceled = data.map((item) => item[11] ?? 0);
  const totalClicksToPay = data.map((item) => item[12] ?? 0);
  const totalClicks = data.map((item) => item[13] ?? 0);
  const totalButtons = data.map((item) => item[14] ?? 0);

  const seriesData = [
    { name: "Notifications", data: data.map((item) => item[5] ?? 0) },
    { name: "Questions", data: data.map((item) => item[6] ?? 0) },
    { name: "Referrals", data: data.map((item) => item[7] ?? 0) },
    { name: "Canceled", data: data.map((item) => item[8] ?? 0) },
    { name: "All Clicks", data: data.map((item) => item[1] ?? 0) },
    { name: "Digital Clicks", data: data.map((item) => item[2] ?? 0) },
    { name: "Physical Clicks", data: data.map((item) => item[3] ?? 0) },
    { name: "Link Clicks", data: data.map((item) => item[4] ?? 0) },
    { name: "New Buttons", data: data.map((item) => item[10] ?? 0) },
  ];

  // Calculate the min and max values for yAxis
  const allData = seriesData.flatMap((series) => series.data);
  const minY = Math.min(...allData);
  const maxY = Math.max(...allData) / 0.9;

  const yAxisTickInterval = Math.ceil((maxY - minY) / 5);

  const options = {
    chart: {
      type: "line",
      height: CHART_HEIGHT,
      marginLeft: 0,
      marginTop: 18,
    },
    title: {
      text: undefined,
    },
    xAxis: {
      categories: dates,
      title: {
        text: null,
      },
      labels: {
        enabled: false,
      },
      // gridLineWidth: 1,
    },
    yAxis: {
      title: {
        text: null,
      },
      labels: {
        enabled: false,
      },
      min: minY,
      max: maxY,
      enabled: false,
      tickInterval: yAxisTickInterval,
    },
    legend: {
      align: "right",
      verticalAlign: "top",
      layout: isMobile ? "vertical" : "horizontal",
      floating: true,
    },
    series: seriesData,
    credits: {
      enabled: false,
    },
  };

  const yAxisOnlyOptions = {
    chart: {
      type: "line",
      marginTop: 18,
      marginRight: 0,
      height: CHART_HEIGHT,
      width: VERTICAL_AXIS_WIDTH,
    },
    yAxis: {
      title: {
        text: null,
      },
      min: minY,
      max: maxY,
      tickInterval: yAxisTickInterval,
    },
    series: [{ data: [] }],
    title: {
      text: null,
    },
    xAxis: {
      labels: {
        enabled: false,
      },
      title: {
        text: null,
      },
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
  };

  useEffect(() => {
    const chartRef = scrollChartRef.current;
    const tableRef = scrollTableRef.current;

    const chartScroll = (e) => {
      scrollTableRef.current.scrollLeft = scrollChartRef.current.scrollLeft;
    };

    const tableScroll = (e) => {
      scrollChartRef.current.scrollLeft = scrollTableRef.current.scrollLeft;
    };

    if (scrollChartRef.current) {
      scrollChartRef.current.scrollLeft = data.length * 100;
      scrollChartRef.current.addEventListener("scroll", chartScroll);
    }
    if (scrollTableRef.current) {
      scrollTableRef.current.scrollLeft = data.length * 100;
      scrollTableRef.current.addEventListener("scroll", tableScroll);
    }

    return () => {
      chartRef?.removeEventListener("scroll", chartScroll);
      tableRef?.removeEventListener("scroll", tableScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isMobile]);

  const isChartVisible = !loading && data.length > 0;
  const isNoData = !loading && data.length === 0;

  return (
    <div className="flex flex-col justify-start items-stretch">
      <div className="flex flex-row justify-between items-center max-w-full gap-1">
        {isChartVisible && (
          <>
            <ContainerWithTransparentScroll>
              <VerticalAxis yAxisOnlyOptions={yAxisOnlyOptions} />
            </ContainerWithTransparentScroll>
            <Divider orientation="vertical" flexItem />
          </>
        )}
        <ContainerWithTransparentScroll
          ref={scrollChartRef}
          className="flex-1 overflow-y-hidden"
        >
          <div
            style={{
              width: `${data.length * COLUMN_WIDTH}px`,
              minWidth: "100%",
              height: `${CHART_HEIGHT}px`,
            }}
          >
            {isChartVisible && (
              <HighchartsReact highcharts={Highcharts} options={options} />
            )}
            {isNoData && (
              <div className="flex flex-col justify-center items-center w-full h-full text-gray-400">
                No data
              </div>
            )}
          </div>
        </ContainerWithTransparentScroll>
      </div>
      <div className="flex flex-row justify-between items-stretch max-w-full gap-1">
        {isChartVisible && (
          <>
            <div
              className="flex flex-col justify-start items-stretch text-xs text-right divide-y striped"
              style={{
                height: `${TABLE_HEIGHT}px`,
                width: `${VERTICAL_AXIS_WIDTH}px`,
              }}
            >
              <div className="text-blue-500 font-bold">New</div>
              <div>Notifications</div>
              <div>Questions</div>
              <div>Referrals</div>
              <div>Canceled</div>
              <div></div>
              <div>Digital Clicks</div>
              <div>Physical Clicks</div>
              <div>Link Clicks</div>
              <div>All Clicks</div>
              <div>Buttons</div>
              <div></div>
              <div className="text-blue-500 font-bold">Running Total</div>
              <div>Canceleled</div>
              <div>Clicks To Pay</div>
              <div>Clicks</div>
              <div>Buttons</div>
            </div>
            <Divider orientation="vertical" flexItem />
          </>
        )}
        <div
          ref={scrollTableRef}
          className="flex-1 overflow-x-scroll overflow-y-hidden"
        >
          <div
            className="flex flex-row justify-stretch items-stretch text-xs text-center divide-x mr-2"
            style={{
              width: `${data.length * COLUMN_WIDTH}px`,
              minWidth: "100%",
              height: `${TABLE_HEIGHT}px`,
            }}
          >
            {
                // const seriesData = [
                //   { name: "Notifications", data: data.map((item) => item[5] ?? 0) },
                //   { name: "Questions", data: data.map((item) => item[6] ?? 0) },
                //   { name: "Referrals", data: data.map((item) => item[7] ?? 0) },
                //   { name: "Canceled", data: data.map((item) => item[8] ?? 0) },
                //   { name: "All Clicks", data: data.map((item) => item[1] ?? 0) },
                //   { name: "Digital Clicks", data: data.map((item) => item[2] ?? 0) },
                //   { name: "Physical Clicks", data: data.map((item) => item[3] ?? 0) },
                //   { name: "Link Clicks", data: data.map((item) => item[4] ?? 0) },
                //   { name: "New Buttons", data: data.map((item) => item[10] ?? 0) },
                // ];
              
            }
            {!loading && (period !== "Weekly" ? dates : weeks)?.map((item, index) => (
              <div
                key={`subtotal-${index}`}
                className="flex flex-col m-0 p-0 justify-stretch items-stretch divide-y striped w-full"
              >
                <div className="text-gray-600 text-xs">{item}</div>
                <div>{seriesData[0].data[index]}</div>
                <div>{seriesData[1].data[index]}</div>
                <div>{seriesData[2].data[index]}</div>
                <div>{seriesData[3].data[index]}</div>
                <div></div>
                <div>{seriesData[5].data[index]}</div>
                <div>{seriesData[6].data[index]}</div>
                <div>{seriesData[7].data[index]}</div>
                <div>{seriesData[4].data[index]}</div>
                <div>{seriesData[8].data[index]}</div>
                <div></div>
                <div className="text-white">{seriesData[3].data[index]}</div>
                <div>{totalCanceled[index]}</div>
                <div>{totalClicksToPay[index]}</div>
                <div>{totalClicks[index]}</div>
                <div>{totalButtons[index]}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
